import { ConsultationStatus, CONSULTATION_RECORD_STATUS, type ConsultationRecordStatus } from '@/enums/quotation'
import type {
  FeeMeta,
  FeeMetaAmountSnapshot,
  FeeMetaTierChange,
  QuotationConsultationChangeVO
} from '@/api/quotation/quotation-base'

/**
 * 空运磋商记录标准化工具（与 express-consultation-records 对称）。
 *
 * 与 express 的差异：
 * - express 把一条费用拆成 N 条 change（首重/续重/单价/重量段...）
 * - 空运一条费用 = 一条 change，并通过 fee_meta JSON 承载完整变更信息
 *   - AIR_BASE：单 densityLevel + 多档位（tiers[]）
 *   - 其他单值费用：before/after
 */

/** 空运 feeType（与 constants.ts FEE_TYPE 对齐） */
export const AIR_FEE_TYPE = {
  AIR_BASE: 0,
  AIR_SURCHARGE: 1,
  AIR_ORIGIN: 2,
  AIR_DEST: 3,
  AIR_POTENTIAL: 4,
  PRE_PORT_BASE: 10,
  PRE_PORT_SURCHARGE: 11,
  PRE_PORT_POTENTIAL: 12,
  LAST_MILE_BASE: 20,
  LAST_MILE_SURCHARGE: 21,
  LAST_MILE_POTENTIAL: 22
} as const

/** 11 个费用分类展示顺序：先 PRE_PORT → AIR → LAST_MILE（与详情页 Tab 顺序一致） */
export const AIR_FEE_TYPE_ORDER: number[] = [
  AIR_FEE_TYPE.PRE_PORT_BASE,
  AIR_FEE_TYPE.PRE_PORT_SURCHARGE,
  AIR_FEE_TYPE.PRE_PORT_POTENTIAL,
  AIR_FEE_TYPE.AIR_BASE,
  AIR_FEE_TYPE.AIR_SURCHARGE,
  AIR_FEE_TYPE.AIR_ORIGIN,
  AIR_FEE_TYPE.AIR_DEST,
  AIR_FEE_TYPE.AIR_POTENTIAL,
  AIR_FEE_TYPE.LAST_MILE_BASE,
  AIR_FEE_TYPE.LAST_MILE_SURCHARGE,
  AIR_FEE_TYPE.LAST_MILE_POTENTIAL
]

export interface NormalizedAirConsultationRecord {
  id?: string | number
  consultationVersion: number
  dateCreated?: string
  /** 期望价格（从 remarks 中解析） */
  expectedPrice: string
  /** 磋商备注（从 remarks 中解析） */
  remark: string
  /** 状态：磋商中 / 已回复 */
  status: ConsultationRecordStatus
  /** 5 大费用分类聚合 */
  groupedChanges: AirFeeChangeGroup[]
  raw: any
}

/** 5 大分类下的变更组 */
export interface AirFeeChangeGroup {
  feeType: number
  feeTypeName: string
  /** 该分类下所有变更项 */
  changes: AirChangeItem[]
}

/** 单条变更项（一条 change 渲染为一个或多个 change item） */
export interface AirChangeItem {
  /** 变更类型 */
  type: 'added' | 'removed' | 'adjusted'
  /** 费用项 ID（用于 BaseDataDisplay 反查名称） */
  chargeItemId?: string
  /** 费用项名称兜底（chargeItemId 为空时使用） */
  chargeItemName?: string
  /** 币种 */
  currency?: string
  /** 仅 AIR_BASE 有：密度等级 */
  densityLevel?: string
  /** 仅 AIR_BASE 有：档位编码 */
  tier?: string
  /** 仅 AIR_BASE 有：档位展示文案 */
  tierLabel?: string
  /** 旧金额（已格式化为字符串） */
  oldAmount?: string
  /** 新金额（已格式化为字符串） */
  newAmount?: string
  /** 原始记录 */
  raw: QuotationConsultationChangeVO
}

type Translate = (key: string, params?: Record<string, unknown>) => string

const hasValue = (value: unknown) => value !== null && value !== undefined && value !== ''

const toNumber = (value: unknown) => {
  if (!hasValue(value)) return null
  const result = Number(value)
  return Number.isFinite(result) ? result : null
}

const toTimestamp = (value: unknown) => {
  if (!hasValue(value)) return 0
  const result = new Date(String(value)).getTime()
  return Number.isFinite(result) ? result : 0
}

const EXPECTED_PRICE_RE = /(?:期望价格|目标价格|Expected\s*Price)\s*[:：]\s*([^\r\n]+)/i
const EXPECTED_PRICE_LINE_RE = /^\s*(?:期望价格|目标价格|Expected\s*Price)\s*[:：]/i
const NOTE_LABEL_RE = /^\s*(?:磋商说明|备注|Negotiation\s*Note|Note)\s*[:：]\s*/i

const parseExpectedPrice = (remarks: unknown) => {
  const text = String(remarks || '')
  return text.match(EXPECTED_PRICE_RE)?.[1]?.trim() || '-'
}

const parseRemark = (remarks: unknown) => {
  const text = String(remarks || '')
  if (!text) return '-'
  const result = text
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(line => line && !EXPECTED_PRICE_LINE_RE.test(line))
    .map(line => line.replace(NOTE_LABEL_RE, '').trim())
    .filter(Boolean)
    .join('\n')
  return result || '-'
}

/** 从 plan 上获取磋商记录原始列表（兼容多字段命名） */
export const getAirConsultationRecords = (quotation: any) => {
  const records = quotation?.consultationList || quotation?.negotiationRecords || quotation?.negotiationList || []
  return Array.isArray(records) ? records : []
}

/**
 * 将后端倒序记录转换为页面使用的正序磋商时间线，并按 5 大分类聚合 change。
 */
export const normalizeAirConsultationRecords = (
  records: any[],
  quotationConsultationStatus: unknown
): NormalizedAirConsultationRecord[] => {
  const sorted = records
    .map((record, sourceIndex) => ({ record, sourceIndex }))
    .sort((left, right) => {
      const leftVersion = toNumber(left.record?.consultationVersion)
      const rightVersion = toNumber(right.record?.consultationVersion)
      if (leftVersion !== null && rightVersion !== null && leftVersion !== rightVersion) {
        return leftVersion - rightVersion
      }
      const timeDiff = toTimestamp(left.record?.dateCreated) - toTimestamp(right.record?.dateCreated)
      return timeDiff || left.sourceIndex - right.sourceIndex
    })

  const consulting = Number(quotationConsultationStatus) === ConsultationStatus.IN_PROGRESS
  return sorted.map(({ record }, index) => ({
    ...record,
    raw: record,
    id: record?.id,
    consultationVersion: toNumber(record?.consultationVersion) || index + 1,
    dateCreated: record?.dateCreated || record?.createdAt || record?.createTime,
    expectedPrice: parseExpectedPrice(record?.remarks),
    remark: parseRemark(record?.remarks),
    status: consulting && index === sorted.length - 1 ? CONSULTATION_RECORD_STATUS.CONSULTING : CONSULTATION_RECORD_STATUS.REPLIED,
    groupedChanges: groupAirChangesByFeeType(Array.isArray(record?.changeList) ? record.changeList : [])
  }))
}

/**
 * 把 changeList 按 feeType 聚合（无变更的分类不返回）。
 * 顺序遵循 AIR_FEE_TYPE_ORDER：先 PRE_PORT → AIR → LAST_MILE。
 */
export const groupAirChangesByFeeType = (
  changeList: QuotationConsultationChangeVO[] = []
): AirFeeChangeGroup[] => {
  const groupMap = new Map<number, AirFeeChangeGroup>()

  changeList.forEach(change => {
    const items = buildAirChangeItems(change)
    if (!items.length) return
    const feeType = items[0].raw.feeMeta?.feeType
    if (feeType === undefined || feeType === null) return
    if (!groupMap.has(feeType)) {
      groupMap.set(feeType, {
        feeType,
        feeTypeName: items[0].raw.feeMeta?.feeTypeName || '',
        changes: []
      })
    }
    groupMap.get(feeType)!.changes.push(...items)
  })

  // 按 AIR_FEE_TYPE_ORDER 排序，仅返回有变更的分类
  return AIR_FEE_TYPE_ORDER
    .filter(ft => groupMap.has(ft))
    .map(ft => groupMap.get(ft)!)
}

/**
 * 把一条 change 渲染为 1 ~ N 个展示项：
 * - AIR_BASE：按 tiers 展开（每个 tier 一个 item，含 densityLevel）
 * - 单值费用：1 个 item（before/after）
 */
const buildAirChangeItems = (change: QuotationConsultationChangeVO): AirChangeItem[] => {
  const meta = change.feeMeta
  if (!meta) return []

  // AIR_BASE：按 tier 展开
  if (meta.feeType === AIR_FEE_TYPE.AIR_BASE) {
    if (!meta.tiers || !meta.tiers.length) return []
    return meta.tiers.map(tier => buildAirBaseTierItem(change, meta, tier))
  }

  // 单值费用：1 个 item
  const item = buildAirSingleValueItem(change, meta)
  return item ? [item] : []
}

const buildAirBaseTierItem = (
  change: QuotationConsultationChangeVO,
  meta: FeeMeta,
  tier: FeeMetaTierChange
): AirChangeItem => {
  const before = tier.before
  const after = tier.after
  const type = !before && after ? 'added'
    : before && !after ? 'removed'
    : 'adjusted'
  return {
    type,
    chargeItemId: meta.chargeItemId,
    chargeItemName: meta.chargeItemName,
    currency: after?.currency ?? before?.currency ?? meta.currency,
    densityLevel: meta.densityLevel,
    tier: tier.tier,
    tierLabel: tier.tierLabel,
    oldAmount: before ? formatAmount(before) : undefined,
    newAmount: after ? formatAmount(after) : undefined,
    raw: change
  }
}

const buildAirSingleValueItem = (
  change: QuotationConsultationChangeVO,
  meta: FeeMeta
): AirChangeItem | null => {
  const before = meta.before
  const after = meta.after
  if (!before && !after) return null
  const type = !before && after ? 'added'
    : before && !after ? 'removed'
    : 'adjusted'
  return {
    type,
    chargeItemId: meta.chargeItemId,
    chargeItemName: meta.chargeItemName,
    currency: after?.currency ?? before?.currency ?? meta.currency,
    oldAmount: before ? formatAmount(before) : undefined,
    newAmount: after ? formatAmount(after) : undefined,
    raw: change
  }
}

const formatAmount = (snapshot: FeeMetaAmountSnapshot): string | undefined => {
  if (!snapshot || !hasValue(snapshot.amount)) return undefined
  const num = Number(snapshot.amount)
  if (!Number.isFinite(num)) return String(snapshot.amount)
  return num.toFixed(2)
}

/** 变更类型的 i18n 文案 */
export const getAirChangeTypeText = (type: AirChangeItem['type'], t: Translate): string =>
  t(`quotation.supplierAirQuotation.consultationRecords.changeTypes.${type}`)
